<template>
    <BaseDialog
        :showDialog="show"
        maxDialogWidth="760"
        maxCardHeight="780"
        headline="lang_GENERAL_FILE_IMPORT_DIALOG_TITLE_UPLOAD_FILE"
        :close="close"
        :alvqa="alvqa"
    >
        <template v-slot:dialogText>
            <v-container fluid>
<!-- FORM -->
                <v-form v-if="phase === 'form' || phase === 'duplicate'" ref="form" v-model="valid">
                    <v-row>
                        <v-col cols="6">
                            <v-select
                                v-model="dataType"
                                :items="dataTypeItems"
                                :label="$t('lang_PUBLISHING_USAGE_CAPTION_DATA_TYPE')"
                                :rules="[required]"
                                :alvqa="alvqa + '-dropdown-data-type'"
                                @change="loadMappings"
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-select
                                v-model="usageArea"
                                :items="usageAreaItems"
                                :label="$t('lang_PUBLISHING_USAGE_CAPTION_USAGE_AREA')"
                                :alvqa="alvqa + '-dropdown-usage-area'"
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-select
                                v-model="mappingAlvId"
                                :items="mappings"
                                :loading="mappingsLoading"
                                :label="$t('lang_GENERIC_CAPTION_MAPPING')"
                                :rules="[required]"
                                :no-data-text="$t('lang_GENERIC_MESSAGE_WARNING_NO_MAPPINGS_AVAILABLE_FOR_DOMAIN')"
                                :alvqa="alvqa + '-dropdown-mapping'"
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-select
                                v-model="period"
                                :items="periods"
                                :label="$t('lang_GENERIC_CAPTION_PERIOD')"
                                :rules="[required]"
                                :alvqa="alvqa + '-dropdown-period'"
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-select
                                v-model="source"
                                :items="sources"
                                :label="$t('lang_GENERIC_CAPTION_SOURCE')"
                                clearable
                                :hint="$t('lang_PUBLISHING_USAGE_CAPTION_IMPORT_SOURCE_HINT')"
                                persistent-hint
                                :alvqa="alvqa + '-dropdown-source'"
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="description"
                                :label="$t('lang_GENERIC_CAPTION_DESCRIPTION')"
                                counter="255"
                                :rules="[v => !v || v.length <= 255 || $t('lang_PUBLISHING_USAGE_MESSAGE_ERROR_IMPORT_DESCRIPTION_TOO_LONG')]"
                                :alvqa="alvqa + '-textfield-description'"
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-file-input
                                v-model="file"
                                :label="$t('lang_GENERAL_CAPTION_SELECT_FILE')"
                                :rules="[required]"
                                accept=".csv,.xlsx,.xls,.txt"
                                show-size
                                :alvqa="alvqa + '-file-input'"
                            />
                        </v-col>
                    </v-row>
                    <v-row v-if="phase === 'duplicate'">
                        <v-col cols="12">
                            <v-alert color="standardBrownIcon" icon="warning_amber" dense border="left" colored-border class="mb-0" :alvqa="alvqa + '-duplicate-warning'">
                                {{ $t('lang_PUBLISHING_USAGE_MESSAGE_WARNING_IMPORT_DUPLICATE', { fileName: duplicate.fileName, importedAt: duplicate.importedAt }) }}
                            </v-alert>
                        </v-col>
                    </v-row>
                    <v-row v-if="errorText">
                        <v-col cols="12">
                            <v-alert type="error" dense text class="mb-0" :alvqa="alvqa + '-error'">
                                {{ errorText }}
                                <ul v-if="fieldErrors.length" class="mt-1">
                                    <li v-for="(line, i) in fieldErrors" :key="i">{{ line }}</li>
                                </ul>
                            </v-alert>
                        </v-col>
                    </v-row>
                </v-form>

<!-- RUNNING -->
                <v-row v-if="phase === 'running'">
                    <v-col cols="12" class="text-center" :alvqa="alvqa + '-running-text'">
                        {{ $t('lang_GENERAL_FILE_UPLOAD_DIALOG_TEXT_FILE_IS_BEING_PROCESSED') }}
                    </v-col>
                    <v-col cols="12">
                        <v-progress-linear
                            :value="percent"
                            height="25"
                            color="dialogProgressbar"
                            :alvqa="alvqa + '-progressbar'"
                        >{{ percent }} %</v-progress-linear>
                    </v-col>
                    <v-col cols="12" class="caption subtitleGray--text text-center">
                        {{ $t('lang_PUBLISHING_USAGE_CAPTION_RUNNING_HINT') }}
                    </v-col>
                </v-row>

<!-- RESULT -->
                <v-row v-if="phase === 'done' && result">
                    <v-col cols="12">
                        <v-alert
                            :type="result.importStatus === 'imported' ? 'success' : 'error'"
                            dense text class="mb-0"
                            :alvqa="alvqa + '-result'"
                        >
                            <template v-if="result.importStatus === 'imported'">
                                {{ $t('lang_PUBLISHING_USAGE_MESSAGE_SUCCESS_IMPORT_DONE', {
                                    stored: $n(result.importSummary ? result.importSummary.stored : result.rowCount),
                                    fileName: result.fileName
                                }) }}
                            </template>
                            <template v-else>{{ result.importError || $t('lang_PUBLISHING_USAGE_CAPTION_STATUS_FAILED_HINT') }}</template>
                        </v-alert>
                    </v-col>
                    <v-col v-if="rejectedReasons.length" cols="12">
                        <div class="subtitle-2 mb-1">{{ $t('lang_PUBLISHING_USAGE_CAPTION_IMPORT_REJECTED', { count: $n(result.importSummary.rejected) }) }}</div>
                        <v-simple-table dense style="background-color: transparent">
                            <tbody>
                                <tr v-for="row in rejectedReasons" :key="row.reason">
                                    <td>{{ row.reason }}</td>
                                    <td class="text-right">{{ $n(row.count) }}</td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-col>
                </v-row>
            </v-container>
        </template>

        <template v-slot:dialogActions>
            <v-spacer />
            <BaseButton
                v-if="phase === 'form'"
                label="lang_GENERIC_CAPTION_IMPORT"
                icon="cloud_upload"
                :loading="submitting"
                :disabled="!valid"
                :alvqa="alvqa + '-button-import'"
                @click="submit(false)"
            />
            <template v-if="phase === 'duplicate'">
                <BaseButton
                    label="lang_GENERAL_BUTTON_CANCEL"
                    color="buttonCancel"
                    class="mr-2"
                    :alvqa="alvqa + '-button-cancel'"
                    @click="phase = 'form'"
                />
                <BaseButton
                    label="lang_GENERAL_BUTTON_UPLOAD_FILE"
                    :loading="submitting"
                    :alvqa="alvqa + '-button-import-again'"
                    @click="submit(true)"
                />
            </template>
            <BaseButton
                v-if="phase === 'running' || phase === 'done'"
                label="lang_GENERAL_BUTTON_CLOSE"
                :alvqa="alvqa + '-button-close'"
                @click="close"
            />
        </template>
    </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/_base/BaseDialog'
import BaseButton from '@/components/_buttons/BaseButton'
import usageApi from '@/api/usageApi'
import { usageErrorText } from '@/store/modules/2_publishing/usage'
import { DATA_TYPES, USAGE_AREAS, SOURCES, IMPORT_DOMAINS, periodOptions } from '../usageOptions'

const POLL_MS = 1500

export default {
    name: 'UsageImportDialog',
    components: { BaseDialog, BaseButton },
    data() {
        return {
            alvqa: 'publishing-usage-import-dialog',
            phase: 'form', // form | duplicate | running | done
            valid: false,
            submitting: false,
            dataType: null,
            usageArea: 'film_tv',
            mappingAlvId: null,
            mappings: [],
            mappingsLoading: false,
            period: String(new Date().getFullYear()),
            source: null,
            description: '',
            file: null,
            duplicate: {},
            errorText: '',
            fieldErrors: [],
            percent: 0,
            polling: null,
            result: null
        }
    },
    computed: {
        show() { return this.$store.getters.getShowUsageImportDialog },
        dataTypeItems() { return DATA_TYPES.map(o => ({ value: o.value, text: this.$t(o.text) })) },
        usageAreaItems() { return USAGE_AREAS.map(o => ({ value: o.value, text: this.$t(o.text) })) },
        sources() { return SOURCES },
        periods() { return periodOptions() },
        rejectedReasons() {
            const byReason = this.result && this.result.importSummary ? this.result.importSummary.rejectedByReason : null
            if (!byReason) return []
            return Object.entries(byReason).map(([reason, count]) => ({ reason, count }))
        }
    },
    watch: {
        show(show) {
            if (!show) return
            this.reset()
            this.dataType = this.$store.getters.getSelectedUsageImport
            this.source = this.dataType === 'usage_statement' ? 'GEMA' : null
            this.loadMappings()
        }
    },
    methods: {
        required(value) { return !!value || this.$t('lang_GENERAL_VALIDATION_MESSAGE_FIELD_REQUIRED') },
        reset() {
            clearInterval(this.polling)
            Object.assign(this, { phase: 'form', submitting: false, mappingAlvId: null, mappings: [], description: '', file: null, duplicate: {}, errorText: '', fieldErrors: [], percent: 0, result: null })
        },
        async loadMappings() {
            this.mappings = []
            this.mappingAlvId = null
            if (!this.dataType) return
            this.mappingsLoading = true
            const response = await usageApi.mappings('import', IMPORT_DOMAINS[this.dataType])
            this.mappingsLoading = false
            this.mappings = (response && Array.isArray(response.list) ? response.list : [])
                .map(m => ({ value: m.alvId, text: m.title + ' (' + m.alvId + ')' }))
            if (this.mappings.length === 1) this.mappingAlvId = this.mappings[0].value
        },
        async submit(allowDuplicate) {
            this.errorText = ''
            this.fieldErrors = []
            this.submitting = true
            const form = new FormData()
            form.append('dataType', this.dataType)
            form.append('usageArea', this.usageArea)
            form.append('mappingAlvId', this.mappingAlvId)
            form.append('period', this.period)
            if (this.source) form.append('source', this.source)
            if (this.description) form.append('description', this.description)
            if (allowDuplicate) form.append('allowDuplicate', '1')
            form.append('file', this.file)
            const response = await usageApi.import(form)
            this.submitting = false
            if (!response.ok) {
                if (response.error.code === 'duplicate_dataset') {
                    this.duplicate = response.error.details
                    this.phase = 'duplicate'
                    return
                }
                this.phase = 'form'
                this.errorText = usageErrorText(response.error)
                this.fieldErrors = Object.entries(response.error.details.fields || {}).map(([field, text]) => field + ': ' + text)
                return
            }
            this.phase = 'running'
            this.$store.dispatch('fetchUsage')
            this.poll(response.data.datasetId, response.data.processId)
        },
        poll(datasetId, processId) {
            this.polling = setInterval(async () => {
                const progress = await usageApi.progress(processId)
                if (progress && typeof progress.percent === 'number') this.percent = progress.percent
                const detail = await usageApi.detail(datasetId)
                if (!detail.ok || detail.data.importStatus === 'importing') return
                clearInterval(this.polling)
                this.percent = 100
                this.result = detail.data
                this.phase = 'done'
                this.$store.dispatch('fetchUsage')
            }, POLL_MS)
        },
        close() {
            // An import keeps running in the background; the list shows its state
            clearInterval(this.polling)
            this.$store.commit('setShowUsageImportDialog', false)
            this.$store.commit('setSelectedUsageImport', null)
        }
    },
    beforeDestroy() {
        clearInterval(this.polling)
    }
}
</script>
